import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Card, Dato, Seccion } from '@/components/Card';
import { GraficaSerie } from '@/components/GraficaSerie';
import { Segmentado } from '@/components/Segmentado';
import { leerProgresoDeEjercicio } from '@/db/records';
import { fechaCorta } from '@/domain/hoy';
import {
  COMO_SALE_METRICA,
  CORTO_METRICA,
  NOMBRE_LARGO_METRICA,
  NOMBRE_METRICA,
  puntosDeMetrica,
  textoDeCambio,
  textoDeMetrica,
  textoMostrado,
  unidadDeMetrica,
  valorEnUnidad,
  type Metrica,
} from '@/domain/records';
import { useFechaHoy } from '@/lib/useFechaHoy';

/**
 * Progreso y récords de un ejercicio (Req. 25.1, 25.3): el mejor valor de
 * cada métrica con el día en que se logró, y la gráfica por sesión.
 *
 * Nada de esto se guarda: sale de las series cada vez, así que corregir una
 * serie corrige el récord. Las cargas en placas no entran, porque el número
 * de placa de una máquina no se compara con otra.
 */
export function ProgresoDeEjercicio({ exerciseId }: { exerciseId: string }) {
  const hoy = useFechaHoy();
  const progreso = useLiveQuery(() => leerProgresoDeEjercicio(exerciseId), [exerciseId]);
  const [elegidaPorMi, setElegida] = useState<Metrica | null>(null);
  if (progreso === undefined) return null;
  // Sin sesiones no hay nada que mostrar: el historial de abajo ya lo dice
  if (progreso === null || progreso.sesiones.length === 0) return null;

  // La carga se lee en la unidad con que se captura ese ejercicio, como su
  // historial y su rutina: así el récord se puede comparar con lo que anota
  const { records, metricas, sesiones, enPlacas, unidad } = progreso;
  const elegida =
    elegidaPorMi !== null && metricas.includes(elegidaPorMi) ? elegidaPorMi : (metricas[0] ?? null);
  const puntos =
    elegida === null
      ? []
      : puntosDeMetrica(sesiones, elegida).map((p) => ({ fecha: p.fecha, valor: valorEnUnidad(elegida, p.valor, unidad) }));

  return (
    <Seccion
      titulo="Tu progreso"
      descripcion="Sale de tus series: las de calentamiento no cuentan. Corregir una serie corrige el récord."
      extra={`${sesiones.length} ${sesiones.length === 1 ? 'sesión' : 'sesiones'}`}
    >
      {records.length === 0 ? (
        <p className="rounded-panel bg-superficie p-5 text-[13px] leading-relaxed text-texto-suave">
          {enPlacas > 0
            ? 'Todavía no hay récords: las cargas en placas no se comparan, porque cada máquina es distinta.'
            : 'Todavía no hay récords: faltan series de trabajo con sus datos.'}
        </p>
      ) : (
        <div className="space-y-4">
          <Card>
            <span className="antetitulo">Tus récords</span>
            <dl className="mt-2 divide-y divide-borde">
              {records.map((r) => (
                <Dato
                  key={r.metrica}
                  etiqueta={NOMBRE_METRICA[r.metrica]}
                  valor={textoDeMetrica(r.metrica, r.valor, unidad)}
                  detalle={`${fechaCorta(r.fecha)}${r.modalidad === 'casa' ? ', en casa' : ''}. ${COMO_SALE_METRICA[r.metrica]}`}
                />
              ))}
            </dl>
            {enPlacas > 0 && (
              <p className="mt-3 border-t border-borde pt-3 text-[12px] leading-relaxed text-texto-suave">
                {enPlacas === 1 ? '1 serie en placas no entra' : `${enPlacas} series en placas no entran`}: cada máquina es
                distinta y su número de placa no es un peso.
              </p>
            )}
          </Card>

          {elegida !== null && (
            <div className="space-y-3">
              {metricas.length > 1 && (
                <Segmentado
                  etiqueta="Qué muestra la gráfica"
                  valor={elegida}
                  opciones={metricas.map((m) => ({
                    valor: m,
                    etiqueta: CORTO_METRICA[m],
                    nombre: NOMBRE_LARGO_METRICA[m],
                  }))}
                  onCambio={(m) => setElegida(m)}
                  completo
                  compacto
                />
              )}
              <GraficaSerie
                etiqueta={`Periodo de la gráfica de ${CORTO_METRICA[elegida].toLocaleLowerCase('es-MX')}`}
                puntos={puntos}
                nombrePuntos={NOMBRE_LARGO_METRICA[elegida]}
                formatear={(v) => textoMostrado(elegida, v, unidad)}
                formatearCambio={(v) => textoDeCambio(elegida, v, unidad)}
                textoCantidad={(n) => `${n} ${n === 1 ? 'día' : 'días'} con este ejercicio`}
                unidad={unidadDeMetrica(elegida, unidad)}
                vacio="En este periodo no hiciste este ejercicio."
                hoy={hoy}
                rangoInicial="6m"
              />
            </div>
          )}
        </div>
      )}
    </Seccion>
  );
}
